import { useCallback, useState } from "react";
import { Alert, Linking } from "react-native";
import * as Location from "expo-location";
import analytics from "@react-native-firebase/analytics";

import { type AppUserSession } from "@/schemas/auth/session";
import ProfileService from "@/services/user/profile";

interface LocationPermissionState {
  location: Location.LocationObject | null;
  permissionGranted: boolean;
  lastRequestTime: Date | null;
}

interface LocationPermissionHook extends LocationPermissionState {
  requestLocationPermission: (
    session: AppUserSession,
    alertTitle?: string,
    alertMessage?: string,
    useCooldown?: boolean,
  ) => Promise<void>;
}

const LOCATION_PERMISSION_COOLDOWN = 60 * 60 * 1000; // 60 minutes

// This is a hook because if the user denies the request;
// we could later on make the request in a different screen like maps or explore.
// Ideally, the screen can have the context on which error message to show.

export function useLocationPermission(): LocationPermissionHook {
  const [state, setState] = useState<LocationPermissionState>({
    lastRequestTime: null,
    location: null,
    permissionGranted: false,
  });

  const requestLocationPermission = useCallback(
    async (
      session: AppUserSession,
      alertTitle = "Location Access Needed",
      alertMessage = "Location access is needed to improve your experience. You can enable it in your device settings.",
      useCooldown = true,
    ) => {
      const now = new Date();

      if (
        useCooldown &&
        state.lastRequestTime &&
        now.getTime() - state.lastRequestTime.getTime() <
          LOCATION_PERMISSION_COOLDOWN
      ) {
        return;
      }

      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== Location.PermissionStatus.GRANTED) {
        await analytics().logEvent("app_location_disabled");
        Alert.alert(alertTitle, alertMessage, [
          { style: "cancel", text: "Not Now" },
          {
            onPress: () => Linking.openSettings(),
            text: "Open Settings",
          },
        ]);
        setState((prevState) => ({
          ...prevState,
          lastRequestTime: now,
          permissionGranted: false,
        }));
        return;
      }

      try {
        await analytics().logEvent("app_location_enabled");
        const location = await Location.getCurrentPositionAsync({});
        setState({ lastRequestTime: now, location, permissionGranted: true });
        const profileService = new ProfileService(session);
        await profileService.registerGPSCoordinates(location);
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          lastRequestTime: now,
          permissionGranted: true,
        }));
      }
    },
    [state.lastRequestTime],
  );

  return { ...state, requestLocationPermission };
}
